import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { LoadingIcon, TopDropdown } from "../../components";
import {
  getCrops,
  getGreenhouses,
  getHarvestDates,
  getOrgs,
  getPlots,
  getSites,
} from "../../utils/api";
import { UserContext } from "../../context/UserContext";
import { useLocalStorage } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../consts";

const Dropdowns = ({
  setSiteMetadata,
  setCurrentMetric,
  setMetricType,
  fetchGraphData,
  fetchSubplotGraphData,
  currentOrg,
  setCurrentOrg,
  currentSite,
  setCurrentSite,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  currentDate,
  setCurrentDate,
  setMetricData,
  plotData,
  setPlotData,
  setDropdownLoading,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [orgData, setOrgData] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  const [siteData, setSiteData] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);
  // const [plotData, setPlotData] = useState([]);
  const [cropData, setCropData] = useState([]);
  const [cropLoading, setCropLoading] = useState(false);

  const [plotLoading, setPlotLoading] = useState(false);

  const [dateData, setDateData] = useState([]);
  const [settingDefaults, setSettingDefaults] = useState({});

  const [searchParams, setSearchParams] = useSearchParams();

  const getGreenhouseId = (plot) =>
    plot?.greenhouse_id ||
    (typeof plot?.greenhouse === "object" ? plot.greenhouse?.id : plot?.greenhouse) ||
    null;

  const getGreenhouseName = (plot) =>
    plot?.greenhouse_name ||
    (typeof plot?.greenhouse === "object" ? plot.greenhouse?.name : null) ||
    null;

  const getPlotValue = (plot) => {
    const greenhouseId = getGreenhouseId(plot);
    return greenhouseId ? `${greenhouseId}---${plot.id}` : plot.id;
  };

  const findPlot = (plotId, greenhouseId = null) =>
    (greenhouseId &&
      plotData?.find((p) => p.id === plotId && getGreenhouseId(p) === greenhouseId)) ||
    plotData?.find((p) => p.id === plotId);

  // ---------------------------------------------
  //  API Calls
  // ---------------------------------------------
  const fetchOrgs = async () => {
    setOrgLoading(true);
    const res = await getOrgs();
    const newData = res.data.data
      .filter((org) => org.is_active && org.is_breeder)
      .map((org) => ({
        name: org.name,
        id: org.id,
      }));
    setOrgData(newData);
    setOrgLoading(false);
  };

  const fetchSites = async (orgId) => {
    setSiteLoading(true);
    const res = await getSites(orgId);
    const data = res.data.data
      .filter((s) => s.is_active)
      .map((s) => ({
        name: s.name,
        displayName: s.display_name,
        id: s.id,
        metadata: s.metadata,
      }));
    setSiteData(data);
    setSiteLoading(false);
  };

  const fetchCrops = async (siteId) => {
    setCropLoading(true);
    const res = await getCrops(siteId);
    const cropList = Array.isArray(res?.data?.data) ? res.data.data : [];
    const data = cropList.filter((c) => c.is_active).map((c) => ({
      name: c.name,
      displayName: c.display_name,
      id: c.id,
    }));
    setCropData(data);
    setCropLoading(false);
  };

  const fetchPlots = async (orgId, siteId, cropId, dateString) => {
    if (!orgId || !siteId || !cropId || !dateString) return;
    setPlotLoading(true);
    const [res, greenhouseRes] = await Promise.all([
      getPlots(orgId, siteId, cropId, dateString),
      getGreenhouses(orgId, siteId, cropId),
    ]);
    const plotList = Array.isArray(res?.data?.data) ? res.data.data : [];
    const greenhouseList = Array.isArray(greenhouseRes?.data?.data)
      ? greenhouseRes.data.data
      : [];
    const greenhousePlots = greenhouseList.flatMap((greenhouse) =>
      (greenhouse.plots || [])
        .filter((plot) =>
          (plot.harvests || []).some((harvest) => harvest.date === dateString),
        )
        .map((plot) => ({
          ...plot,
          greenhouse_id: greenhouse.id,
          greenhouse_name: greenhouse.name,
        })),
    );
    const data = (greenhousePlots.length ? greenhousePlots : plotList).map(
      (plot) => ({
        ...plot,
        name: plot.name,
        id: plot.id,
        greenhouse_id: getGreenhouseId(plot),
        greenhouse_name: getGreenhouseName(plot),
      }),
    );
    setPlotData(data);
    setPlotLoading(false);
  };

  const fetchHarvestDates = async (orgId, siteId, cropId) => {
    const res = await getHarvestDates(orgId, siteId, cropId);
    const dateList = Array.isArray(res?.data?.data) ? res.data.data : [];
    const dates = dateList.map((d) => d.date);
    setDateData(dates);
  };

  // ---------------------------------------------
  //  Date logic
  // ---------------------------------------------
  const disabledDate = (current) => {
    // only allow selection of existing harvest dates
    const dayjsDates = dateData.map((date) => dayjs(date));
    return (
      current &&
      !dayjsDates.reduce((enabled, date) => {
        return enabled || date.isSame(current, "day");
      }, false)
    );
  };

  const handleDateChange = (newDate, dateString) => {
    if (newDate) {
      const backendDateStr = newDate.format(BACKEND_DATE_FORMAT);
      setCurrentDate(backendDateStr);

      const params = new URLSearchParams(searchParams);
      params.set("date", backendDateStr);
      setSearchParams(params);
    } else {
      setCurrentDate(null);
    }
  };

  // ---------------------------------------------
  //  On mount: fetch orgs or set currentOrg
  // ---------------------------------------------
  useEffect(() => {
    if (!userData) return;
    async function initData() {
      try {
        if (userData.is_superuser) {
          await fetchOrgs();
        } else {
          setCurrentOrg(userData.organization_id);

          const res = await getSites(userData.organization_id);
          const siteList = Array.isArray(res?.data?.data) ? res.data.data : [];
          const filtered = siteList.filter((s) => s.is_active);
          const mapped = filtered.map((s) => ({
            name: s.name,
            displayName: s.display_name,
            id: s.id,
            metadata: s.metadata,
          }));
          setSiteData(mapped);
        }
      } catch (error) {
        console.error("Error fetching data on mount:", error);
      }
    }
    initData();
  }, [userData]);

  // If superuser => fetch sites when org changes
  useEffect(() => {
    if (currentOrg && userData?.is_superuser) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  // Whenever site changes => fetch crops
  useEffect(() => {
    if (currentSite) {
      fetchCrops(currentSite.id);
    } else {
      setCurrentCrop(null);
    }
  }, [currentSite]);

  // Whenever site/crop/org/date changes => fetch plots
  useEffect(() => {
    if (currentSite && currentOrg) {
      fetchPlots(currentOrg, currentSite.id, currentCrop?.id, currentDate);
    }
  }, [currentSite, currentCrop, currentOrg, currentDate]);

  // Whenever site/crop/org changes => fetch harvest dates
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop) {
      fetchHarvestDates(currentOrg, currentSite.id, currentCrop.id);
    }
  }, [currentOrg, currentSite, currentCrop]);

  // When site/crop/plot are all set => set siteMetadata, fetchGraphData
  useEffect(() => {
    if (currentSite && currentCrop && currentPlot) {
      // fetch subplot graph data if ripe_criteria is present
      const ripeCriteria =
        currentSite.metadata[currentCrop.name]?.ripe_criteria;
      if (ripeCriteria) {
        fetchSubplotGraphData();
      }
      // set siteMetadata
      const meta = currentSite.metadata[currentCrop.name] || {};
      setSiteMetadata(meta);

      // set MetricData
      const metrics = meta.metrics
        ? Object.entries(meta.metrics).map(([type, arr]) => ({
            type,
            metrics: arr,
          }))
        : [];
      setMetricData(metrics);

      fetchGraphData();
    } else {
      setMetricData([]);
    }
  }, [currentSite, currentCrop, currentPlot]);

  // ---------------------------------------------
  //  ID-based default selection (Org, Site, Crop, Plot, Date)
  // ---------------------------------------------
  // 1) Org
  useEffect(() => {
    if (orgData.length) {
      const params = new URLSearchParams(searchParams);
      const orgIdFromUrl = params.get("org");

      if (orgIdFromUrl) {
        const foundOrg = orgData.find((o) => o.id === orgIdFromUrl);
        setCurrentOrg(
          foundOrg ? foundOrg.id : defaultDropdown?.org || orgData[0]?.id,
        );
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else if (!settingDefaults.org) {
        setCurrentOrg(defaultDropdown?.org || orgData[0].id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else {
        setCurrentOrg(orgData[0]?.id);
      }
    }
  }, [orgData]);

  // 2) Site
  useEffect(() => {
    if (siteData.length) {
      const params = new URLSearchParams(searchParams);
      const siteIdFromUrl = params.get("site");

      if (siteIdFromUrl) {
        const foundSite = siteData.find((s) => s.id === siteIdFromUrl);
        setCurrentSite(foundSite || siteData[0]);
      } else if (!settingDefaults.site) {
        const storedSiteId = defaultDropdown?.[currentOrg]?.site;
        if (storedSiteId) {
          const foundSite = siteData.find((s) => s.id === storedSiteId);
          setCurrentSite(foundSite || siteData[0]);
        } else {
          setCurrentSite(siteData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, site: true }));
      } else {
        setCurrentSite(siteData[0]);
      }
    } else {
      setCurrentSite(null);
    }
  }, [siteData]);

  // 3) Crop
  useEffect(() => {
    if (cropData.length) {
      const params = new URLSearchParams(searchParams);
      const cropIdFromUrl = params.get("crop");

      if (cropIdFromUrl) {
        const foundCrop = cropData.find((c) => c.id === cropIdFromUrl);
        setCurrentCrop(foundCrop || cropData[0]);
      } else if (!settingDefaults.crop) {
        const storedCropId = defaultDropdown?.[currentOrg]?.crop;
        if (storedCropId) {
          const foundCrop = cropData.find((c) => c.id === storedCropId);
          setCurrentCrop(foundCrop || cropData[0]);
        } else {
          setCurrentCrop(cropData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, crop: true }));
      } else {
        setCurrentCrop(cropData[0]);
      }
    } else {
      setCurrentCrop(null);
    }
  }, [cropData]);

  // 4) Plot
  useEffect(() => {
    if (plotData?.length) {
      const params = new URLSearchParams(searchParams);
      const plotIdFromUrl = params.get("plot");
      const greenhouseIdFromUrl = params.get("greenhouse");

      if (plotIdFromUrl) {
        const foundPlot = findPlot(plotIdFromUrl, greenhouseIdFromUrl);
        setCurrentPlot(foundPlot || plotData[0]);
      } else if (!settingDefaults.plot) {
        const storedPlotId = defaultDropdown?.[currentOrg]?.plot;
        const storedGreenhouseId = defaultDropdown?.[currentOrg]?.greenhouse;
        if (storedPlotId) {
          const foundPlot = findPlot(storedPlotId, storedGreenhouseId);
          setCurrentPlot(foundPlot || plotData[0]);
        } else {
          setCurrentPlot(plotData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, plot: true }));
      } else {
        setCurrentPlot(plotData[0]);
      }
    } else {
      setCurrentPlot(null);
    }
  }, [plotData]);

  // 5) Date
  useEffect(() => {
    if (!dateData?.length) return;
    const params = new URLSearchParams(searchParams);
    const dateFromUrl = params.get("date");

    if (dateFromUrl && dateData.includes(dateFromUrl)) {
      setCurrentDate(dateFromUrl);
    } else {
      const first = dateData[0] || null;
      setCurrentDate(first);
      if (first) {
        params.set("date", first);
        setSearchParams(params);
      }
    }
  }, [dateData]);

  // ---------------------------------------------
  //  Sync dropdown changes to localStorage + URL
  // ---------------------------------------------
  // Org
  useEffect(() => {
    if (currentOrg) {
      const params = new URLSearchParams(searchParams);
      params.set("org", currentOrg);
      setSearchParams(params);
      setDefaultDropdown({ ...defaultDropdown, org: currentOrg });
    }
  }, [currentOrg]);

  // Site
  useEffect(() => {
    if (currentSite) {
      const params = new URLSearchParams(searchParams);
      params.set("site", currentSite.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          site: currentSite.id,
        },
      });
    }
  }, [currentSite]);

  // Crop
  useEffect(() => {
    if (currentCrop) {
      const params = new URLSearchParams(searchParams);
      params.set("crop", currentCrop.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          crop: currentCrop.id,
        },
      });
    }
  }, [currentCrop]);

  // Plot
  useEffect(() => {
    if (currentPlot) {
      const params = new URLSearchParams(searchParams);
      params.set("plot", currentPlot.id);
      const greenhouseId = getGreenhouseId(currentPlot);
      if (greenhouseId) params.set("greenhouse", greenhouseId);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouse: greenhouseId || orgObj.greenhouse,
          plot: currentPlot.id,
        },
      });
    }
  }, [currentPlot]);

  // Date
  useEffect(() => {
    if (currentDate) {
      const params = new URLSearchParams(searchParams);
      params.set("date", currentDate);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          date: currentDate,
        },
      });
    }
  }, [currentDate]);

  // ---------------------------------------------
  //  Update metric defaults from site metadata
  // ---------------------------------------------
  useEffect(() => {
    if (currentSite && currentCrop) {
      let val =
        currentSite.metadata?.[currentCrop.name]?.default_metrics?.[0] || null;
      setMetricType(val);
    } else {
      setMetricType(null);
    }
  }, [currentSite, currentCrop]);

  useEffect(() => {
    if (currentSite && currentCrop) {
      let val =
        currentSite.metadata?.[currentCrop.name]?.default_metrics?.[1] || null;
      setCurrentMetric(val);
    } else {
      setCurrentMetric(null);
    }
  }, [currentSite, currentCrop]);

  // ---------------------------------------------
  //  Overall loading
  // ---------------------------------------------
  useEffect(() => {
    setDropdownLoading(
      orgLoading || siteLoading || cropLoading || plotLoading || !currentDate,
    );
  }, [
    orgLoading,
    siteLoading,
    cropLoading,
    plotLoading,
    currentDate,
    setDropdownLoading,
  ]);

  // ---------------------------------------------
  //  Render
  // ---------------------------------------------
  return (
    <>
      {orgLoading || siteLoading || plotLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns !font-MuseoSans">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          <TopDropdown
            label="Select Plot:"
            val={currentPlot ? getPlotValue(currentPlot) : undefined}
            placeholder="Select Plot"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(value) => {
              const [greenhouseId, plotId] = value.includes("---")
                ? value.split("---")
                : [null, value];
              const plotObj = findPlot(plotId, greenhouseId);
              setCurrentPlot(plotObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={plotData?.map((plot) => ({
              label: plot.greenhouse_name
                ? `${plot.greenhouse_name}: ${plot.name}`
                : plot.name,
              value: getPlotValue(plot),
            }))}
          />

          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Date:
            </div>
            <DatePicker
              value={currentDate ? dayjs(currentDate) : null}
              className="flex-1 min-w-[150px] sm:mb-2"
              disabledDate={disabledDate}
              onChange={handleDateChange}
              format={UI_DATE_FORMAT}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdowns;
